import { Card, CardContent } from "./ui/card";
import { ShieldCheck, Sparkles, Heart, Info } from "lucide-react";

/**
 * "How we choose your products" — sits at the top of the report page.
 *
 * Builds trust BEFORE users read individual product cards. Plain language,
 * no medical jargon, clear about what we do and don't do.
 */
export default function HowWeChooseBlock() {
  return (
    <section data-testid="how-we-choose-block" aria-labelledby="how-we-choose-heading">
      <Card className="rounded-2xl border-amber-200/60 bg-gradient-to-br from-amber-50/40 via-white to-emerald-50/40">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-700 text-white shrink-0">
              <Sparkles className="w-4 h-4" />
            </span>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                How we choose your products
              </div>
              <h2 id="how-we-choose-heading" className="font-display text-xl sm:text-2xl text-emerald-950 mt-1 leading-tight">
                These recommendations are matched to <em>you</em>, not to the highest commission.
              </h2>
            </div>
          </div>

          <p className="text-sm sm:text-base text-emerald-900/80 leading-relaxed">
            Your assessment answers, BMI, lifestyle and stated goals are run through our matching
            engine. We score every product in our catalogue against your profile and surface
            only the ones that genuinely fit. You'll see exactly <strong>why</strong> each one was
            chosen below — in plain language, with no clinical jargon.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            <Pillar
              icon={<Heart className="w-4 h-4" />}
              title="Built around your assessment"
              body="Every answer matters — we weight goals, lifestyle and risk indicators together."
            />
            <Pillar
              icon={<ShieldCheck className="w-4 h-4" />}
              title="No paid placement"
              body="We never bump products up the list because they pay more. Best fit wins."
            />
            <Pillar
              icon={<Info className="w-4 h-4" />}
              title="Wellness, not medical advice"
              body="If you have a diagnosed condition, please involve your GP — we complement, never replace, clinical care."
            />
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function Pillar({ icon, title, body }) {
  return (
    <div className="rounded-xl border border-emerald-100 bg-white/70 backdrop-blur-sm p-4">
      <div className="flex items-center gap-2 text-emerald-700">
        {icon}
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold">{title}</div>
      </div>
      <p className="text-xs sm:text-sm text-emerald-900/80 leading-relaxed mt-2">{body}</p>
    </div>
  );
}
